class ProductCardAltCarousel {
	constructor(element) {
		try {
			this.card = element;
			this.carousel = this.card.querySelector('[data-carousel]');
			
			if (!this.carousel) return;
			
			this.colorButtons = this.card.querySelectorAll('[data-color-variant]');
			this.variantImagesData = null;
			
			// Parse variant images data safely
			try {
				if (this.card.dataset.variantImages) {
					this.variantImagesData = JSON.parse(this.card.dataset.variantImages);
				}
			} catch (e) {
				console.warn('Failed to parse variant images data', e);
			}
			
			this.currentColorValue = null;
			this.allSlides = [];
			this.allPaginationBars = [];
			this.visibleImageIds = [];
			
			this.track = this.carousel.querySelector('[data-carousel-track]');
			this.slides = Array.from(this.carousel.querySelectorAll('.product-card-alt__carousel-slide'));
			this.prevBtn = this.carousel.querySelector('[data-carousel-prev]');
			this.nextBtn = this.carousel.querySelector('[data-carousel-next]');
			this.paginationBars = Array.from(this.carousel.querySelectorAll('[data-pagination-bar]'));
			
			this.allSlides = [...this.slides];
			this.allPaginationBars = [...this.paginationBars];
			
			this.currentIndex = 0;
			this.userInteracted = false;
			this.pointerStartX = 0;
			this.pointerStartY = 0;
			this.activePointerId = null;
			this.lastSwipeAt = 0;
			this.totalSlides = this.slides.length;
			this.card.productCardAltCarousel = this;
			
			// Bind methods to maintain context
			this.handlePrevClick = this.handlePrevClick.bind(this);
			this.handleNextClick = this.handleNextClick.bind(this);
			this.handleMouseEnter = this.handleMouseEnter.bind(this);
			this.handleMouseLeave = this.handleMouseLeave.bind(this);
			this.handlePointerDown = this.handlePointerDown.bind(this);
			this.handlePointerUp = this.handlePointerUp.bind(this);
			this.handlePointerCancel = this.handlePointerCancel.bind(this);
			this.handleCarouselClick = this.handleCarouselClick.bind(this);
			
			// Set initial color if color variants exist
			if (this.colorButtons.length > 0) {
				const activeButton = Array.from(this.colorButtons).find(btn => btn.classList.contains('active'));
				if (activeButton) {
					this.currentColorValue = activeButton.dataset.colorVariant;
					this.filterImagesByColor(this.currentColorValue, false);
				}
			} else if (this.card.dataset.defaultColor) {
				this.currentColorValue = this.card.dataset.defaultColor;
				this.filterImagesByColor(this.currentColorValue, false);
			}
			
			// A card without selectable colors does not need carousel behavior when
			// its default variant has only one visible image.
			if (this.totalSlides <= 1 && this.colorButtons.length === 0) {
				return;
			}
			
			this.init();
		} catch (error) {
			console.error('Error initializing ProductCardAltCarousel:', error, element);
		}
	}
	
	handlePrevClick(e) {
		e.preventDefault();
		e.stopPropagation();
		this.userInteracted = true;
		this.goToPrev();
	}
	
	handleNextClick(e) {
		e.preventDefault();
		e.stopPropagation();
		this.userInteracted = true;
		this.goToNext();
	}
	
	handleMouseEnter() {
		if (window.innerWidth >= 1024 && !this.userInteracted && this.slides.length > 1) {
			this.goToSlide(1);
		}
	}
	
	handleMouseLeave() {
		if (window.innerWidth >= 1024 && !this.userInteracted && this.slides.length > 1) {
			this.goToSlide(0);
		}
	}

	handlePointerDown(e) {
		if (window.innerWidth >= 1024 || !e.isPrimary || e.pointerType === 'mouse' || this.slides.length <= 1) return;

		this.activePointerId = e.pointerId;
		this.pointerStartX = e.clientX;
		this.pointerStartY = e.clientY;
	}

	handlePointerUp(e) {
		if (e.pointerId !== this.activePointerId) return;

		const deltaX = e.clientX - this.pointerStartX;
		const deltaY = e.clientY - this.pointerStartY;
		this.activePointerId = null;

		if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

		this.userInteracted = true;
		this.lastSwipeAt = performance.now();

		if (deltaX < 0) {
			this.goToNext();
		} else {
			this.goToPrev();
		}
	}

	handlePointerCancel(e) {
		if (e.pointerId === this.activePointerId) {
			this.activePointerId = null;
		}
	}

	handleCarouselClick(e) {
		if (performance.now() - this.lastSwipeAt > 500) return;

		e.preventDefault();
		e.stopPropagation();
		this.lastSwipeAt = 0;
	}
	
	init() {
		// Arrow navigation
		if (this.prevBtn) {
			this.prevBtn.addEventListener('click', this.handlePrevClick);
		}
		
		if (this.nextBtn) {
			this.nextBtn.addEventListener('click', this.handleNextClick);
		}
		
		// Hover behavior for desktop - always attach, check width in handler
		if (this.carousel) {
			this.carousel.addEventListener('mouseenter', this.handleMouseEnter);
			this.carousel.addEventListener('mouseleave', this.handleMouseLeave);
			this.carousel.addEventListener('pointerdown', this.handlePointerDown);
			this.carousel.addEventListener('pointerup', this.handlePointerUp);
			this.carousel.addEventListener('pointercancel', this.handlePointerCancel);
			this.carousel.addEventListener('click', this.handleCarouselClick, true);
		}
		
		// Pagination click for mobile. Use the current data index because
		// filtering color variants can change a bar's visible position.
		this.allPaginationBars.forEach((bar) => {
			bar.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.userInteracted = true;
				this.goToSlide(Number.parseInt(bar.dataset.paginationBar, 10));
			});
		});
		
		// Color variant buttons
		this.colorButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				const colorValue = button.dataset.colorVariant;
				
				// Update active state
				this.colorButtons.forEach(btn => btn.classList.remove('active'));
				button.classList.add('active');
				
				// Filter images by color
				this.filterImagesByColor(colorValue, true);
			});
		});
	}
	
	filterImagesByColor(colorValue, resetCarousel) {
		if (!this.variantImagesData || !colorValue) return;
		
		this.currentColorValue = colorValue;
		const imageIdsForColor = this.variantImagesData[colorValue];
		
		if (!imageIdsForColor || imageIdsForColor.length === 0) {
			// Show all images if no specific images for this variant
			this.visibleImageIds = this.allSlides.map(slide => slide.dataset.imageId);
		} else {
			this.visibleImageIds = imageIdsForColor;
		}
		
		// Update visible slides
		let visibleIndex = 0;
		this.slides = [];
		const newPaginationBars = [];
		
		this.allSlides.forEach((slide, originalIndex) => {
			const imageId = slide.dataset.imageId;
			const isVisible = this.visibleImageIds.includes(imageId);
			
			if (isVisible) {
				slide.style.display = '';
				slide.dataset.slideIndex = visibleIndex;
				this.slides.push(slide);
				
				if (this.allPaginationBars[originalIndex]) {
					this.allPaginationBars[originalIndex].style.display = '';
					this.allPaginationBars[originalIndex].dataset.paginationBar = visibleIndex;
					newPaginationBars.push(this.allPaginationBars[originalIndex]);
				}
				
				visibleIndex++;
			} else {
				slide.style.display = 'none';
				if (this.allPaginationBars[originalIndex]) {
					this.allPaginationBars[originalIndex].style.display = 'none';
				}
			}
		});
		
		this.paginationBars = newPaginationBars;
		
		this.totalSlides = this.slides.length;
		
		// Show/hide navigation based on visible slide count
		const carouselNav = this.carousel.querySelector('.product-card-alt__carousel-nav');
		const pagination = this.carousel.querySelector('.product-card-alt__pagination');
		
		if (this.totalSlides <= 1) {
			if (carouselNav) carouselNav.style.display = 'none';
			if (pagination) pagination.style.display = 'none';
		} else {
			if (carouselNav) carouselNav.style.display = '';
			if (pagination) pagination.style.display = '';
		}
		
		// Reset to the first image in the newly filtered slide set.
		this.currentIndex = 0;
		if (resetCarousel) {
			this.userInteracted = false;
		}
		this.updateSlides();
	}
	
	goToSlide(index) {
		if (index < 0 || index >= this.totalSlides) return;
		this.currentIndex = index;
		this.updateSlides();
	}
	
	goToNext() {
		this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
		this.updateSlides();
	}
	
	goToPrev() {
		this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
		this.updateSlides();
	}
	
	updateSlides() {
		this.slides.forEach((slide, index) => {
			slide.classList.toggle('active', index === this.currentIndex);
		});
		
		this.paginationBars.forEach((bar, index) => {
			bar.classList.toggle('active', index === this.currentIndex);
		});
	}
}

// Initialize all product cards
function initializeProductCards() {
	const cards = document.querySelectorAll('.product-card-alt');
	
	cards.forEach((card) => {
		try {
			if (!card.dataset.carouselInitialized) {
				new ProductCardAltCarousel(card);
				card.dataset.carouselInitialized = 'true';
			}
		} catch (error) {
			console.error('Failed to initialize card', error);
		}
	});
}

function setProductCardAltColor(card, colorValue) {
	if (!card || !colorValue) return;

	card.dataset.defaultColor = colorValue;
	const carousel = card.productCardAltCarousel;

	if (carousel) {
		carousel.currentColorValue = colorValue;
		carousel.filterImagesByColor(colorValue, true);
		return;
	}

	delete card.dataset.carouselInitialized;
	initializeProductCards();
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initializeProductCards);
} else {
	// DOM already loaded
	initializeProductCards();
}

// Re-initialize for dynamically loaded content
if (typeof window.initProductCardAlt === 'undefined') {
	window.initProductCardAlt = initializeProductCards;
}

window.setProductCardAltColor = setProductCardAltColor;

document.addEventListener('pagination:page-changed', initializeProductCards);
